import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Camera, AlertTriangle, Plus, FileText, ChevronDown, ChevronRight,
  Sparkles,
} from "lucide-react";
import { api, formatApiError } from "../../lib/api";

const MONTH_PT = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun",
                  "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

function fmtDate(iso) {
  if (!iso || iso.length < 10) return "—";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `${parseInt(d)} ${MONTH_PT[parseInt(m) - 1]} ${y}`;
}

/**
 * /cliente/historico — Cleaning timeline.
 * Vertical lifeline of every completed cleaning the host has had with us.
 * Each card collapses by default and expands to show the full photo grid +
 * issues/extras counters + invoice link. Designed for hosts who want to
 * show inquilinos the cleaning history at a glance.
 */
export default function ClientTimeline() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/client/timeline");
        if (!cancelled) setData(data);
      } catch (_) {} finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) return <div className="text-[#a0b2af]">A carregar histórico…</div>;

  const items = data?.items || [];
  const summary = data?.summary || {};

  return (
    <div className="space-y-10" data-testid="client-timeline">
      <header className="space-y-3">
        <div className="eyebrow">Passport · Histórico de limpezas</div>
        <h1 className="font-display text-4xl md:text-5xl">A sua linha do tempo.</h1>
        <p className="text-sm text-[#a0b2af] max-w-xl">
          Cada limpeza realizada fica aqui registada com fotos, problemas reportados
          e a fatura associada — útil para mostrar aos seus inquilinos a frequência
          e qualidade da manutenção do seu alojamento.
        </p>
      </header>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryTile label="Limpezas concluídas"
                     value={summary.total_cleanings || 0}
                     testId="timeline-stat-cleanings" />
        <SummaryTile label="Fotos partilhadas"
                     value={summary.total_photos || 0}
                     testId="timeline-stat-photos" />
        <SummaryTile label="Primeira limpeza"
                     value={summary.first_cleaning ? fmtDate(summary.first_cleaning) : "—"}
                     small testId="timeline-stat-first" />
        <SummaryTile label="Última limpeza"
                     value={summary.last_cleaning ? fmtDate(summary.last_cleaning) : "—"}
                     small testId="timeline-stat-last" />
      </div>

      {/* Timeline */}
      {items.length === 0 ? (
        <div className="lux-card p-12 text-center" data-testid="timeline-empty">
          <Sparkles size={32} className="mx-auto text-[#d4af37] mb-4" />
          <p className="text-[#a0b2af] mb-5">Ainda não tem limpezas concluídas.</p>
          <Link to="/cliente/agendar" className="btn-gold px-6 py-3 text-sm inline-flex items-center gap-2">
            Agendar primeira limpeza <ChevronRight size={16} />
          </Link>
        </div>
      ) : (
        <ol className="relative border-l-2 border-[#1a4640] pl-6 space-y-6">
          {items.map((b) => (
            <TimelineCard
              key={b.id}
              booking={b}
              open={openId === b.id}
              onToggle={() => setOpenId(openId === b.id ? null : b.id)}
            />
          ))}
        </ol>
      )}
    </div>
  );
}

function SummaryTile({ label, value, small, testId }) {
  return (
    <div className="lux-card p-5" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest text-[#a0b2af]">{label}</div>
      <div className={`font-display ${small ? "text-lg" : "text-3xl"} text-[#d4af37] mt-1.5`}>
        {value}
      </div>
    </div>
  );
}

function TimelineCard({ booking, open, onToggle }) {
  const photos = booking.photos_preview || [];
  const dateStr = fmtDate(booking.scheduled_date);

  return (
    <li className="relative" data-testid={`timeline-row-${booking.id}`}>
      {/* Lifeline dot */}
      <span className="absolute -left-[35px] top-3 w-4 h-4 rounded-full bg-[#d4af37] border-4 border-[#031c18]" />

      <article className="lux-card overflow-hidden">
        <button
          type="button"
          onClick={onToggle}
          className="w-full px-5 py-4 flex items-center gap-4 text-left hover:bg-[#062c27] transition-colors"
          data-testid={`timeline-toggle-${booking.id}`}>
          <div className="flex-1 min-w-0">
            <div className="text-[#d4af37] eyebrow text-[10px] mb-1">{dateStr} · {booking.scheduled_time}</div>
            <div className="font-display text-xl truncate">
              {booking.apartment_name || "Alojamento"}
              <span className="text-[#a0b2af] font-sans text-sm font-normal ml-2">· {booking.apt_type}</span>
            </div>
          </div>
          <div className="flex items-center gap-4 shrink-0 text-xs text-[#a0b2af]">
            <Counter icon={Camera} value={booking.photos_count} label="fotos" />
            <Counter icon={AlertTriangle} value={booking.issues_count} label="problemas"
                     accent={booking.issues_count > 0 ? "#f59e0b" : undefined} />
            <Counter icon={Plus} value={booking.extras_count} label="extras" />
            {booking.invoice && (
              <span className="text-[#d4af37]">{booking.invoice.number}</span>
            )}
            <ChevronDown size={16}
                        className={`text-[#a0b2af] transition-transform ${open ? "rotate-180" : ""}`} />
          </div>
        </button>

        {open && (
          <div className="border-t border-[#1a4640] p-5 space-y-5"
               data-testid={`timeline-body-${booking.id}`}>
            {photos.length > 0 ? (
              <div>
                <div className="eyebrow mb-3">Galeria da limpeza</div>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                  {photos.map(p => (
                    <a key={p.id}
                       href={`${api.defaults.baseURL}/files/${p.storage_path}`}
                       target="_blank" rel="noreferrer"
                       className="block aspect-square overflow-hidden rounded-sm bg-[#031c18] border border-[#1a4640] hover:border-[#d4af37] transition-colors">
                      <img
                        src={`${api.defaults.baseURL}/files/${p.storage_path}`}
                        alt={p.room || "foto"}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </a>
                  ))}
                </div>
                {booking.photos_count > photos.length && (
                  <Link to={`/cliente/reservas/${booking.id}/relatorio`}
                        className="inline-block mt-4 text-xs uppercase tracking-widest text-[#d4af37]">
                    Ver as {booking.photos_count} fotos completas →
                  </Link>
                )}
              </div>
            ) : (
              <div className="text-sm text-[#a0b2af] italic">Esta limpeza não tem fotos arquivadas.</div>
            )}

            <div className="flex flex-wrap gap-3 pt-2 border-t border-[#1a4640]">
              <Link to={`/cliente/reservas/${booking.id}`}
                    className="btn-ghost-gold px-4 py-2 text-xs inline-flex items-center gap-2"
                    data-testid={`timeline-detail-${booking.id}`}>
                Detalhes da reserva <ChevronRight size={12} />
              </Link>
              {booking.invoice && booking.invoice.pdf_url && (
                <a href={booking.invoice.pdf_url}
                   target="_blank" rel="noreferrer"
                   className="btn-ghost-gold px-4 py-2 text-xs inline-flex items-center gap-2">
                  <FileText size={12} /> Fatura {booking.invoice.number}
                </a>
              )}
              <span className="ml-auto text-xs self-center uppercase tracking-widest text-[#d4af37]">
                Valor a combinar
              </span>
            </div>
          </div>
        )}
      </article>
    </li>
  );
}

function Counter({ icon: Icon, value, label, accent }) {
  if (!value) return null;
  return (
    <span className="inline-flex items-center gap-1" style={{ color: accent }}
          title={`${value} ${label}`}>
      <Icon size={12} /> {value}
    </span>
  );
}
